import { DOCUMENT } from '@angular/common';
import {
    EnvironmentProviders,
    inject,
    makeEnvironmentProviders,
    provideAppInitializer,
} from '@angular/core';

export function provideNumericInputGuard(): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideAppInitializer(() => {
            const doc = inject(DOCUMENT);
            installListeners(doc);
        }),
    ]);
}

const NAVIGATION_KEYS = new Set([
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'PageUp',
    'PageDown',
]);

function isNumericInput(target: EventTarget | null): target is HTMLInputElement {
    return target instanceof HTMLInputElement && target.type === 'number';
}

function installListeners(doc: Document): void {
    doc.addEventListener('keydown', onKeyDown, true);
    doc.addEventListener('paste', onPaste, true);
    doc.addEventListener('wheel', onWheel, { capture: true, passive: true });
}

function onKeyDown(event: KeyboardEvent): void {
    if (!isNumericInput(event.target)) return;

    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (NAVIGATION_KEYS.has(event.key)) return;

    const input = event.target;
    const key = event.key;

    if (/^[0-9]$/.test(key)) return;

    if (key === '-' && input.selectionStart === 0 && !input.value.includes('-')) {
        return;
    }

    if (key === '.' && !input.value.includes('.')) {
        return;
    }

    event.preventDefault();
}

function onPaste(event: ClipboardEvent): void {
    if (!isNumericInput(event.target)) return;
    const text = event.clipboardData?.getData('text') ?? '';
    if (text === '') return;
    if (!/^-?\d*\.?\d*$/.test(text) || text === '-' || text === '.' || text === '-.') {
        event.preventDefault();
    }
}

function onWheel(event: WheelEvent): void {
    if (!isNumericInput(event.target)) return;
    if (event.target.ownerDocument.activeElement === event.target) {
        event.target.blur();
    }
}
